import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/auth/AuthProvider";
import { isAdminRole } from "@/lib/permissions";
import { formatDateTime } from "@/lib/format";
import {
  isPendingRule,
  useApproveSopRule,
  useEditSopRule,
  useRejectSopRule,
  useSopDocuments,
  useSopRules,
  type SopRule,
  type SopRuleUpdate,
} from "@/api/queries/sop-documents";
import { DocumentStatusBadge } from "./SopDocumentsPage";

/**
 * SOP rule review (RES-58) — THE human gate. Every extracted rule renders with
 * its VERBATIM quote (never paraphrased), an honest category badge, and the
 * metric mapping the evaluator would score. Nothing scores until a person
 * approves it here; approve/reject are deliberate two-step confirms, per rule,
 * with no bulk action by design.
 */

export function SopDocumentReviewPage() {
  const { identity } = useAuth();
  if (!isAdminRole(identity?.role)) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">Rule review</h1>
        </header>
        <p className="text-sm text-destructive">
          SOP rule review is restricted to chiefs and RespAI administrators.
        </p>
      </div>
    );
  }
  return <SopDocumentReviewPageInner />;
}

function SopDocumentReviewPageInner() {
  const { id } = useParams<{ id: string }>();
  const documents = useSopDocuments();
  const rules = useSopRules(id);
  const doc = documents.data?.find((d) => d.id === id);

  const pending = (rules.data ?? []).filter(isPendingRule);
  const approved = (rules.data ?? []).filter((r) => r.status === "approved");
  const rejected = (rules.data ?? []).filter((r) => r.status === "rejected");

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="space-y-2">
        <Link
          to="/sop-documents"
          className="text-sm text-muted-foreground hover:underline"
        >
          ← SOP Documents
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">
            {doc ? doc.filename : "Rule review"}
          </h1>
          {doc ? <DocumentStatusBadge status={doc.status} /> : null}
          {pending.length > 0 ? (
            <Badge variant="warning">
              {pending.length} pending review
            </Badge>
          ) : null}
        </div>
        <p className="text-sm text-muted-foreground">
          Each rule quotes your SOP verbatim. Approving a rule puts it on the
          department&apos;s compliance report; nothing is scored until you do.
        </p>
      </header>

      {rules.isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      ) : rules.isError ? (
        <p className="text-sm text-destructive">
          Failed to load rules
          {rules.error instanceof Error ? `: ${rules.error.message}` : ""}.
        </p>
      ) : (rules.data ?? []).length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">
              No rules extracted from this document yet. Run &ldquo;Extract
              rules&rdquo; from the SOP Documents page first.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {pending.length > 0 ? (
            <section className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Pending review ({pending.length})
              </h2>
              {pending.map((rule) => (
                <RuleReviewCard key={rule.id} rule={rule} />
              ))}
            </section>
          ) : (
            <p className="text-sm text-muted-foreground">
              No rules pending review.
            </p>
          )}
          {approved.length > 0 ? (
            <ReviewedSection
              title={`Approved (${approved.length})`}
              rules={approved}
            />
          ) : null}
          {rejected.length > 0 ? (
            <ReviewedSection
              title={`Rejected (${rejected.length})`}
              rules={rejected}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

/* ─────────────────────────── Category + metric ──────────────────────────── */

/** Honest category labels — C is never dressed up as scored. */
export function CategoryBadge({ category }: { category: string }) {
  switch (category) {
    case "A":
      return <Badge>A · Instrumented</Badge>;
    case "B":
      return <Badge variant="secondary">B · Procedural</Badge>;
    case "C":
      return <Badge variant="outline">C · Declared, not instrumented</Badge>;
    default:
      return <Badge variant="outline">{category}</Badge>;
  }
}

const METRIC_LABELS: Record<string, string> = {
  co_ppm: "CO (ppm)",
  voc_ppm: "Total VOC (ppm)",
  lel_pct: "Combustible gas (%LEL)",
  o2_pct: "Oxygen (%)",
  interior_seconds: "Interior worktime (seconds)",
  exposure_report: "Exposure report per incident",
  decon_on_air: "On-air through decon",
  not_instrumented: "Not instrumented",
};

const COMPARATOR_SYMBOLS: Record<string, string> = {
  gte: "≥",
  gt: ">",
  lte: "≤",
  lt: "<",
  eq: "=",
};

/** Render the machine-checkable mapping as one honest line. */
export function metricSummary(rule: Pick<SopRule, "metric_key" | "threshold">): string {
  if (!rule.metric_key || rule.metric_key === "not_instrumented") {
    return "Cannot be scored. We have no sensor data for this rule.";
  }
  const label = METRIC_LABELS[rule.metric_key] ?? rule.metric_key;
  const t = rule.threshold ?? {};
  const comparator =
    typeof t.comparator === "string"
      ? COMPARATOR_SYMBOLS[t.comparator] ?? t.comparator
      : null;
  const value = typeof t.value === "number" || typeof t.value === "string" ? t.value : null;
  const unit = typeof t.unit === "string" ? ` ${t.unit}` : "";
  if (comparator && value !== null) return `${label} ${comparator} ${value}${unit}`;
  if (typeof t.window_seconds === "number") {
    return `${label} > ${t.window_seconds} s`;
  }
  if (rule.threshold && Object.keys(rule.threshold).length > 0) {
    return `${label} · ${JSON.stringify(rule.threshold)}`;
  }
  return label;
}

/* ─────────────────────────── Pending rule card ──────────────────────────── */

type PendingAction = "none" | "approve" | "reject" | "edit";

function RuleReviewCard({ rule }: { rule: SopRule }) {
  const [action, setAction] = useState<PendingAction>("none");
  const approve = useApproveSopRule();
  const reject = useRejectSopRule();
  const [rejectNote, setRejectNote] = useState("");

  const confidence =
    typeof rule.extraction_json?.confidence === "number"
      ? (rule.extraction_json.confidence as number)
      : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-base">{rule.rule_code}</CardTitle>
          <CategoryBadge category={rule.category} />
          {rule.status === "edited" ? (
            <Badge variant="outline">Edited</Badge>
          ) : null}
        </div>
        {rule.source_citation ? (
          <p className="text-xs text-muted-foreground">{rule.source_citation}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        <blockquote className="border-l-2 border-border pl-3 text-sm italic text-muted-foreground">
          &ldquo;{rule.rule_text}&rdquo;
        </blockquote>
        <p className="text-sm">
          <span className="text-muted-foreground">Scored as: </span>
          {metricSummary(rule)}
        </p>
        <p className="text-xs text-muted-foreground">
          Extracted by {rule.model_id} ({rule.prompt_version})
          {confidence !== null ? ` · confidence ${Math.round(confidence * 100)}%` : ""}
        </p>

        {action === "edit" ? (
          <RuleEditForm rule={rule} onDone={() => setAction("none")} />
        ) : action === "approve" ? (
          <div className="space-y-1 rounded-md border border-border bg-muted/40 p-3">
            <p className="text-sm">
              Approve for scoring? This rule will appear on the
              department&apos;s compliance report.
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={approve.isPending}
                onClick={() =>
                  approve.mutate(rule, { onSuccess: () => setAction("none") })
                }
              >
                {approve.isPending ? "Approving…" : "Confirm approve"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={approve.isPending}
                onClick={() => setAction("none")}
              >
                Cancel
              </Button>
            </div>
            {approve.isError ? (
              <p className="text-xs text-destructive">
                {approve.error instanceof Error
                  ? approve.error.message
                  : "Could not approve rule."}
              </p>
            ) : null}
          </div>
        ) : action === "reject" ? (
          <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
            <p className="text-sm">
              Reject this rule? It is kept as an audit record and never scores.
            </p>
            <Input
              placeholder="Reason (optional)"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                size="sm"
                disabled={reject.isPending}
                onClick={() =>
                  reject.mutate(
                    { rule, review_note: rejectNote },
                    { onSuccess: () => setAction("none") },
                  )
                }
              >
                {reject.isPending ? "Rejecting…" : "Confirm reject"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={reject.isPending}
                onClick={() => setAction("none")}
              >
                Cancel
              </Button>
            </div>
            {reject.isError ? (
              <p className="text-xs text-destructive">
                {reject.error instanceof Error
                  ? reject.error.message
                  : "Could not reject rule."}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setAction("approve")}>
              Approve
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAction("edit")}>
              Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAction("reject")}>
              Reject
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ────────────────────────────── Edit form ───────────────────────────────── */

function RuleEditForm({ rule, onDone }: { rule: SopRule; onDone: () => void }) {
  const edit = useEditSopRule();
  const [ruleText, setRuleText] = useState(rule.rule_text);
  const [citation, setCitation] = useState(rule.source_citation ?? "");
  const [category, setCategory] = useState(rule.category);

  const save = () => {
    const edits: SopRuleUpdate = {};
    if (ruleText !== rule.rule_text) edits.rule_text = ruleText;
    if (citation !== (rule.source_citation ?? "")) edits.source_citation = citation;
    if (category !== rule.category) {
      edits.category = category as "A" | "B" | "C";
      // The backend enforces category C ⇒ not_instrumented; mirror it so the
      // edit doesn't bounce off the 422.
      if (category === "C") edits.metric_key = "not_instrumented";
    }
    if (Object.keys(edits).length === 0) {
      onDone();
      return;
    }
    edit.mutate({ ruleId: rule.id, edits }, { onSuccess: onDone });
  };

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
      <label className="block text-xs font-medium text-muted-foreground">
        Rule text (keep it verbatim from the SOP)
        <textarea
          className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          rows={3}
          value={ruleText}
          onChange={(e) => setRuleText(e.target.value)}
        />
      </label>
      <label className="block text-xs font-medium text-muted-foreground">
        Source citation
        <Input
          className="mt-1"
          value={citation}
          onChange={(e) => setCitation(e.target.value)}
        />
      </label>
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">Category</span>
        {(["A", "B", "C"] as const).map((c) => (
          <Button
            key={c}
            type="button"
            variant={category === c ? "default" : "outline"}
            size="sm"
            onClick={() => setCategory(c)}
          >
            {c}
          </Button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={edit.isPending} onClick={save}>
          {edit.isPending ? "Saving…" : "Save edits"}
        </Button>
        <Button variant="ghost" size="sm" disabled={edit.isPending} onClick={onDone}>
          Cancel
        </Button>
      </div>
      {edit.isError ? (
        <p className="text-xs text-destructive">
          {edit.error instanceof Error ? edit.error.message : "Could not save edits."}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Saving marks the rule as edited. It still needs your approval before it
        scores anything.
      </p>
    </div>
  );
}

/* ─────────────────────────── Reviewed sections ──────────────────────────── */

function ReviewedSection({ title, rules }: { title: string; rules: SopRule[] }) {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      <ul className="space-y-2">
        {rules.map((rule) => (
          <li
            key={rule.id}
            className="rounded-lg border border-border p-3 text-sm"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{rule.rule_code}</span>
              <CategoryBadge category={rule.category} />
              {rule.status === "approved" ? (
                <Badge variant="success">Approved</Badge>
              ) : (
                <Badge variant="destructive">Rejected</Badge>
              )}
              {rule.reviewed_at ? (
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(rule.reviewed_at)}
                </span>
              ) : null}
            </div>
            <p className="mt-1 italic text-muted-foreground">
              &ldquo;{rule.rule_text}&rdquo;
            </p>
            {rule.review_note ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Note: {rule.review_note}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
